import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Send, StopCircle, Bot, User, Sparkles } from 'lucide-react';

import { getModels } from '../api/ai';

export default function ChatPanel({ disabled, messages, streamEvents, onSend, onStop }) {
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('auto');
  const [model, setModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const scrollRef = useRef(null);

  const transcript = useMemo(() => messages || [], [messages]);

  // Load model list from backend
  useEffect(() => {
    let cancelled = false;
    async function loadModels() {
      setModelsLoading(true);
      try {
        const res = await getModels();
        if (cancelled) return;
        const models = Array.isArray(res?.models) ? res.models : [];
        setAvailableModels(models);

        const defaultModel = typeof res?.default === 'string' ? res.default : '';
        setModel((prev) => prev || defaultModel || models[0] || '');
      } catch {
        if (cancelled) return;
        setAvailableModels([]);
        setModel((prev) => prev || 'glm-4-flash');
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    }

    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, streamEvents]);

  const handleSend = () => {
    const text = message.trim();
    if (!text) return;
    setMessage('');
    onSend({ message: text, mode, model });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-[var(--bg-panel)]">
      {/* Settings Bar */}
      <div className="flex items-center gap-2 p-3 border-b border-[var(--border-subtle)] bg-[var(--bg-app)]/50">
        <select 
            value={mode} 
            onChange={(e) => setMode(e.target.value)} 
            disabled={disabled}
            className="text-xs bg-[var(--bg-input)] border border-[var(--border-subtle)] rounded px-2 py-1 text-[var(--text-secondary)] focus:text-[var(--text-primary)] outline-none"
        >
          <option value="auto">Mode: Auto</option>
          <option value="think">Mode: Think</option>
        </select>
        
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          disabled={disabled || modelsLoading}
          className="flex-1 text-xs bg-[var(--bg-input)] border border-[var(--border-subtle)] rounded px-2 py-1 text-[var(--text-secondary)] focus:text-[var(--text-primary)] outline-none"
        >
          {modelsLoading ? <option value="">Loading models...</option> : null}
          {!modelsLoading && availableModels.length === 0 ? (
            <option value={model || ''}>{model || 'glm-4-flash'}</option>
          ) : null}
          {!modelsLoading
            ? availableModels.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))
            : null}
        </select>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={scrollRef}>
        {transcript.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-[var(--text-secondary)] opacity-50">
                <Sparkles size={32} className="mb-2" />
                <p className="text-sm">Start a conversation to build your strategy</p>
            </div>
        )}
        
        {transcript.map((m, idx) => {
           const isUser = m.role === 'user';
           return (
            <div key={`${idx}_${m.created_at || ''}`} className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isUser ? 'bg-[var(--accent-blue-dim)] text-[var(--accent-blue)]' : 'bg-[var(--accent-purple)]/10 text-[var(--accent-purple)]'}`}>
                    {isUser ? <User size={14} /> : <Bot size={14} />}
                </div>
                <div className={`flex flex-col max-w-[85%] ${isUser ? 'items-end' : 'items-start'}`}>
                    <div className="text-[10px] text-[var(--text-secondary)] mb-1 uppercase tracking-wider">
                        {m.source || m.role}
                    </div>
                    <div className={`rounded-2xl px-4 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                        isUser 
                        ? 'bg-[var(--accent-blue)] text-white' 
                        : 'bg-[var(--bg-panel-hover)] text-[var(--text-primary)] border border-[var(--border-subtle)]'
                    }`}>
                        {m.content}
                    </div>
                </div>
            </div>
           );
        })}

        {/* Streaming Events Indicator */}
        {streamEvents && streamEvents.length > 0 && (
            <div className="flex gap-3">
                 <div className="w-8 h-8 rounded-full bg-[var(--accent-purple)]/10 text-[var(--accent-purple)] flex items-center justify-center shrink-0 animate-pulse">
                    <Bot size={14} />
                 </div>
                 <div className="text-xs text-[var(--text-secondary)] font-mono self-center">
                    Processing...
                 </div>
            </div>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-[var(--border-subtle)] bg-[var(--bg-app)]/30">
        <div className="relative">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={3}
              placeholder="Describe your strategy logic..."
              disabled={disabled}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-subtle)] rounded-xl p-3 pr-12 text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] resize-none focus:border-[var(--accent-blue)] focus:ring-1 focus:ring-[var(--accent-blue)] outline-none transition-all"
            />
            <div className="absolute bottom-3 right-3 flex gap-2">
                {disabled ? (
                     <button onClick={onStop} className="p-1.5 rounded-lg bg-[var(--accent-red-dim)] text-[var(--accent-red)] hover:bg-[var(--accent-red)] hover:text-white transition-colors">
                        <StopCircle size={16} />
                     </button>
                ) : (
                    <button 
                        onClick={handleSend} 
                        disabled={!message.trim()}
                        className="p-1.5 rounded-lg bg-[var(--accent-blue)] text-white hover:bg-[#1f6feb] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Send size={16} />
                    </button>
                )}
            </div>
        </div>
        <div className="mt-2 text-[10px] text-[var(--text-secondary)] text-center">
            AI can make mistakes. Review generated graphs.
        </div>
      </div>
    </div>
  );
}
